import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { inventoryAPI } from '@/lib/api';
import { resolveImageUrl } from '@/lib/imageUtils';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, DollarSign, Phone, X, Printer } from 'lucide-react';

const formatPrice = (p) => p ? `$${p.toLocaleString()}` : 'Call';
const formatKM = (km) => km ? `${km.toLocaleString()} KM` : 'N/A';

const specGroups = [
  { title: 'Basics', fields: [
    { key: 'year', label: 'Year' }, { key: 'make', label: 'Make' }, { key: 'model', label: 'Model' },
    { key: 'stock_number', label: 'Stock #' }, { key: 'location', label: 'Location' }, { key: 'condition', label: 'Condition' },
  ]},
  { title: 'Powertrain', fields: [
    { key: 'engine', label: 'Engine' }, { key: 'horsepower', label: 'Horsepower' },
    { key: 'transmission', label: 'Transmission' }, { key: 'rear_ratio', label: 'Rear Ratio' },
  ]},
  { title: 'Chassis & Specs', fields: [
    { key: 'axles', label: 'Axles' }, { key: 'wheelbase', label: 'Wheelbase' },
    { key: 'suspension', label: 'Suspension' }, { key: 'gvwr', label: 'GVWR' },
  ]},
  { title: 'Mileage & Condition', fields: [
    { key: 'mileage_km', label: 'Mileage', format: formatKM }, { key: 'body_type', label: 'Body Type' },
  ]},
  { title: 'Trailer Specs', fields: [
    { key: 'trailer_type', label: 'Type' }, { key: 'trailer_length', label: 'Length' },
    { key: 'axle_count', label: 'Axle Count' }, { key: 'roof_type', label: 'Roof' },
    { key: 'door_type', label: 'Doors' }, { key: 'reefer_details', label: 'Reefer Details' },
  ]},
  { title: 'Pricing', fields: [
    { key: 'price', label: 'Price', format: formatPrice },
  ]},
];

export default function Compare() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const ids = searchParams.get('ids')?.split(',').filter(Boolean) || [];
    if (ids.length === 0) { setLoading(false); return; }
    inventoryAPI.compare(ids).then(r => { setItems(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, [searchParams]);

  const removeItem = (id) => {
    const newItems = items.filter(i => i.id !== id);
    const newIds = newItems.map(i => i.id).join(',');
    navigate(`/compare?ids=${newIds}`, { replace: true });
    setItems(newItems);
  };

  if (loading) return <div className="max-w-7xl mx-auto px-4 py-12"><div className="animate-pulse h-96 bg-zinc-100 rounded-sm" /></div>;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="compare-page">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/inventory')} data-testid="back-to-inventory">
            <ArrowLeft className="w-4 h-4 mr-1" /> Inventory
          </Button>
          <h1 className="text-2xl md:text-3xl font-bold uppercase text-[#0A0A0A]">Compare Vehicles</h1>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => { navigator.clipboard.writeText(window.location.href); }} data-testid="share-comparison">
            Share
          </Button>
          <Button variant="outline" size="sm" onClick={() => window.print()} data-testid="print-comparison">
            <Printer className="w-4 h-4 mr-1" /> Print
          </Button>
        </div>
      </div>

      {items.length === 0 ? (
        <div className="text-center py-20">
          <p className="text-zinc-500 text-lg">No vehicles selected for comparison</p>
          <Button className="mt-4 bg-[#E31937]" onClick={() => navigate('/inventory')}>Browse Inventory</Button>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full border-collapse min-w-[600px]" data-testid="compare-table">
            {/* Vehicle headers */}
            <thead>
              <tr>
                <th className="w-48 p-3 text-left text-xs text-zinc-400 uppercase"></th>
                {items.map(item => (
                  <th key={item.id} className="p-3 text-left min-w-[200px]">
                    <div className="relative">
                      <button className="absolute -top-1 -right-1 text-zinc-400 hover:text-red-500" onClick={() => removeItem(item.id)}>
                        <X className="w-4 h-4" />
                      </button>
                      {item.images?.length > 0 ? (
                        <img
                          src={resolveImageUrl(item.images[0])}
                          alt={item.title}
                          className="w-full h-32 object-cover rounded-sm mb-2 cursor-pointer"
                          onClick={() => navigate(`/inventory/${item.id}`)}
                        />
                      ) : (
                        <div
                          className="w-full h-32 bg-zinc-200 flex flex-col items-center justify-center rounded-sm mb-2 cursor-pointer"
                          onClick={() => navigate(`/inventory/${item.id}`)}
                        >
                          <span className="text-xs font-semibold text-zinc-400 uppercase tracking-wider">Coming Soon</span>
                        </div>
                      )}
                      <h3 className="font-semibold text-sm text-[#0A0A0A] cursor-pointer hover:text-[#E31937]" onClick={() => navigate(`/inventory/${item.id}`)}>
                        {item.title}
                      </h3>
                      <p className="text-lg font-bold text-[#E31937] mt-1">{formatPrice(item.price)}</p>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {specGroups.map(group => {
                const hasData = group.fields.some(f => items.some(item => item[f.key]));
                if (!hasData) return null;
                return [
                  <tr key={`group-${group.title}`}>
                    <td colSpan={items.length + 1} className="bg-zinc-50 px-3 py-2 text-xs font-bold uppercase text-zinc-500 tracking-wider border-t border-b">
                      {group.title}
                    </td>
                  </tr>,
                  ...group.fields.map(field => {
                    const values = items.map(item => {
                      const val = item[field.key];
                      if (val === null || val === undefined || val === '') return null;
                      return field.format ? field.format(val) : String(val);
                    });
                    if (values.every(v => !v)) return null;
                    const allSame = values.filter(Boolean).length > 1 && new Set(values.filter(Boolean)).size === 1;
                    const isDifferent = values.filter(Boolean).length > 1 && new Set(values.filter(Boolean)).size > 1;
                    return (
                      <tr key={field.key} className="border-b border-zinc-100 hover:bg-zinc-50/50">
                        <td className="p-3 text-sm text-zinc-500 font-medium">{field.label}</td>
                        {items.map((item, idx) => (
                          <td key={item.id} className={`p-3 text-sm font-medium ${isDifferent ? 'bg-amber-50/50 text-[#0A0A0A]' : 'text-[#0A0A0A]'}`}>
                            {values[idx] || <span className="text-zinc-300">--</span>}
                          </td>
                        ))}
                      </tr>
                    );
                  }),
                ];
              })}
            </tbody>
          </table>

          {/* CTAs */}
          <div className="mt-8 flex flex-wrap gap-3 justify-center">
            <Button className="bg-[#E31937] hover:bg-[#C4122C] text-white font-bold uppercase text-xs h-10 px-6"
              onClick={() => navigate(`/financing?ids=${items.map(i => i.id).join(',')}`)}
              data-testid="compare-financing"
            >
              <DollarSign className="w-4 h-4 mr-1" /> Request Financing
            </Button>
            <Button variant="outline" className="border-[#E31937] text-[#E31937] font-bold uppercase text-xs h-10 px-6" data-testid="compare-contact">
              <Phone className="w-4 h-4 mr-1" /> Contact Sales
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
